import styled from "styled-components";
import { getIQContentImage } from "../../utils/utility-functions";
import { labelKeyMap } from "../../utils/general-config";

const getIQGridCols = (count) => (count === 4 ? 2 : Math.min(count, 3));

const IQQuestion = ({
  question,
  currentIndex,
  totalQuestions,
  userAnswers,
  isQuizComplete,
  isProcessing,
  currentLocale,
  onSelectAnswer,
}) => {
  const sortedOpts = question.questionOption || [];
  const hasImgs = sortedOpts.some((o) => o.mediaUrl);
  const selected = userAnswers[question.id];

  return (
    <IQQuestionWrapper>
      <IQQuestionHeader $hasImage={!!question.mediaUrl}>
        <IQCounter>
          {currentIndex + 1} / {totalQuestions}
        </IQCounter>

        <IQQuestionText>
          {question.title_EN ? question.title_EN.split("|")[0].trim() : ""}
        </IQQuestionText>
      </IQQuestionHeader>

      {question.mediaUrl ? (
        <IQQuestionImageCard>
          <IQQuestionImage
            src={getIQContentImage(
              question.mediaUrl,
              "questions",
              currentLocale,
            )}
            alt=""
          />
        </IQQuestionImageCard>
      ) : question.title_EN && question.title_EN.includes("|") ? (
        <IQQuestionImageCard isContext>
          <IQQuestionContextText>
            {question.title_EN.split("|")[1].trim()}
          </IQQuestionContextText>
        </IQQuestionImageCard>
      ) : null}

      {hasImgs ? (
        <IQImageOptionsGrid cols={getIQGridCols(sortedOpts.length)}>
          {sortedOpts.map((opt, i) => (
            <IQImageOptionCard
              key={opt.id}
              $isSelected={selected === opt.id}
              $isDisabled={isProcessing && selected !== opt.id}
              type="button"
              onClick={() =>
                !isQuizComplete &&
                !isProcessing &&
                onSelectAnswer(question.id, opt.id)
              }
            >
              <IQOptionBadge $isSelected={selected === opt.id}>
                {String.fromCharCode(65 + i)}
              </IQOptionBadge>
              <IQOptionImageWrapper>
                <img
                  src={getIQContentImage(
                    opt.mediaUrl,
                    "answers",
                    currentLocale,
                  )}
                  alt={opt.label || ""}
                />
              </IQOptionImageWrapper>
            </IQImageOptionCard>
          ))}
        </IQImageOptionsGrid>
      ) : (
        <IQTextOptionsContainer>
          {sortedOpts.map((opt, i) => {
            const labelKey = labelKeyMap[currentLocale];
            const label = opt[labelKey];
            return (
              <IQTextOptionButton
                key={opt.id}
                $isSelected={selected === opt.id}
                $isDisabled={isProcessing && selected !== opt.id}
                type="button"
                onClick={() =>
                  !isQuizComplete &&
                  !isProcessing &&
                  onSelectAnswer(question.id, opt.id)
                }
              >
                <IQOptionBadge $isSelected={selected === opt.id}>
                  {String.fromCharCode(65 + i)}
                </IQOptionBadge>
                <span>{label}</span>
              </IQTextOptionButton>
            );
          })}
        </IQTextOptionsContainer>
      )}
    </IQQuestionWrapper>
  );
};

export default IQQuestion;

const IQQuestionWrapper = styled.div`
  max-width: 660px;
  width: 100%;
  margin: 0 auto;
  padding: 28px 16px 40px;

  @media (max-width: 640px) {
    padding: 0;
  }
`;

const IQQuestionHeader = styled.div`
  max-width: ${({ $hasImage }) => ($hasImage ? "540px" : "100%")};
  margin: ${({ $hasImage }) => ($hasImage ? "0 auto" : "0")};
  width: 100%;
`;

const IQCounter = styled.p`
  font-size: 0.85rem;
  color: #6b7a99;
  margin-bottom: 12px;
`;

const IQQuestionText = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #111318;
  line-height: 1.45;
  margin-bottom: 20px;
  white-space: pre-line;
`;

const IQQuestionImageCard = styled.div`
  background: #ffffff;
  border: ${({ isContext }) => (isContext ? "1px solid #d6dae8" : "none")};
  border-radius: ${({ isContext }) => (isContext ? "8px" : "16px")};
  padding: ${({ isContext }) => (isContext ? "14px 20px" : "24px 20px")};
  margin: ${({ isContext }) => (isContext ? "0 0 20px" : "0 auto 20px")};
  display: flex;
  align-items: center;
  justify-content: ${({ isContext }) => (isContext ? "flex-start" : "center")};
  width: 100%;
  max-width: ${({ isContext }) => (isContext ? "100%" : "540px")};
  min-height: ${({ isContext }) => (isContext ? "auto" : "180px")};
  box-shadow: ${({ isContext }) =>
    isContext ? "none" : "0 1px 6px rgba(0,0,0,.06)"};

  /* CHANGED: Reduce question image card size on mobile only */
  @media (max-width: 640px) {
    padding: ${({ isContext }) => (isContext ? "8px 10px" : "10px")};
    margin-bottom: 10px;
    min-height: ${({ isContext }) => (isContext ? "auto" : "110px")};
  }
`;

const IQQuestionImage = styled.img`
  object-fit: contain;
`;

const IQQuestionContextText = styled.p`
  font-size: 0.9rem;
  color: #111318;
  line-height: 1.6;
  text-align: left;
  white-space: pre-line;
  font-family: "Courier New", Courier, monospace;
`;

const IQTextOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const IQTextOptionButton = styled.button`
  width: 100%;
  background: ${({ $isSelected }) => ($isSelected ? "#EAF0FF" : "#ffffff")};
  border: 2px solid
    ${({ $isSelected }) => ($isSelected ? "#1D3FA3" : "#D6DAE8")};
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: ${({ $isDisabled }) => ($isDisabled ? "not-allowed" : "pointer")};
  text-align: left;
  font-size: 0.95rem;
  color: #111318;
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.45 : 1)};
  transition:
    border-color 0.15s,
    box-shadow 0.15s,
    background 0.15s,
    opacity 0.15s;
  box-shadow: ${({ $isSelected }) =>
    $isSelected ? "0 0 0 4px rgba(29,63,163,.16)" : "none"};

  &:hover {
    border-color: ${({ $isDisabled }) => ($isDisabled ? "#D6DAE8" : "#1d3fa3")};
  }
`;

const IQImageOptionsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 24px auto 24px;
  width: 100%;
  max-width: ${({ cols }) => (cols < 3 ? "480px" : "unset")};
  justify-content: center;

  & > button {
    flex: 0 0
      ${({ cols }) => (cols >= 3 ? "calc(33.33% - 7px)" : "calc(50% - 5px)")};
    min-width: 0;
  }

  /* CHANGED: Reduce grid size only on mobile */
  @media (max-width: 640px) {
    gap: 6px;
    margin-bottom: 12px;

    & > button {
      padding: 4px;
      height: 100px;
      min-height: 0;
      overflow: hidden;
    }
  }
`;

const IQImageOptionCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: ${({ $isSelected }) => ($isSelected ? "#EAF0FF" : "#ffffff")};
  border: 2px solid
    ${({ $isSelected }) => ($isSelected ? "#1D3FA3" : "#D6DAE8")};
  border-radius: 12px;
  padding: 8px 8px 12px;
  cursor: ${({ $isDisabled }) => ($isDisabled ? "not-allowed" : "pointer")};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.45 : 1)};
  transition:
    border-color 0.15s,
    box-shadow 0.15s,
    background 0.15s,
    opacity 0.15s;
  box-shadow: ${({ $isSelected }) =>
    $isSelected ? "0 0 0 4px rgba(29,63,163,.16)" : "none"};

  &:hover {
    border-color: ${({ $isDisabled }) => ($isDisabled ? "#D6DAE8" : "#1d3fa3")};
  }
  @media (max-width: 640px) {
    min-height: 0;
    padding: 4px;
    overflow: hidden;
  }
`;

const IQOptionImageWrapper = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 4px 0;

  img {
    object-fit: contain;
  }

  /* CHANGED: Force answer images to fit the smaller mobile cards */
  @media (max-width: 640px) {
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
    padding: 2px;
    overflow: hidden;

    img {
      display: block;
      width: 100%;
      height: 100%;
      max-width: 100%;
      max-height: 68px;
      min-width: 0;
      min-height: 0;
      object-fit: contain;
    }
  }
`;

const IQOptionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 700;
  font-family: monospace;
  color: ${({ $isSelected }) => ($isSelected ? "#1D3FA3" : "#8B92A3")};
  flex-shrink: 0;

  @media (max-width: 640px) {
    padding: 4px;
  }
`;
